<template>
  <q-markup-table
    separator="cell"
    flat
    bordered
    dense
    :class="'size-'+size"
  >
    <thead>
      <tr>
        <th />
        <th>Total</th>
        <th>Per Ticket</th>
        <th>Margin</th>
      </tr>
    </thead>
    <tbody>
      <!-- Revenue -->
      <tr class="total-row">
        <td class="bold-label">
          Revenue ({{ $n(pnlData.showingCount, 'decimal0') }} showings)
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.revenue / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.revenue / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr>
        <td class="sub-label">
          Box Office Revenue ({{ $i18n.n(pnlData.ticketsSold, 'decimal0') }} tickets)
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.boxOfficeRevenue / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.boxOfficeRevenue / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr>
        <td
          v-if="pnlData.concessionCount"
          class="sub-label"
        >
          Concession Revenue ({{ $i18n.n(pnlData.concessionCount, 'decimal0') }} items)
        </td>
        <td
          v-else
          class="sub-label"
        >
          Concession Revenue
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.concessionRevenue / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.concessionRevenue / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr>
        <td
          class="bg-grey-2"
          colspan="4"
        />
      </tr>

      <!-- Cost -->
      <tr class="total-row">
        <td class="bold-label">
          Cost of Goods Sold
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.concessionCost / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.concessionCost / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr>
        <td class="sub-label">
          Box Office Cost of Goods Sold
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.boxOfficeCost / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.boxOfficeCost / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td
          v-if="pnlData.boxOfficeRevenue"
          class="cell-value"
          :class="{ 'text-red': pnlData.boxOfficeCost < 0 }"
        >
          <i18n-n
            :value="pnlData.boxOfficeCost / pnlData.boxOfficeRevenue * 100"
            format="decimal0"
            scope="global"
          />%
        </td>
        <td
          v-else
          class="cell-value"
        >
          n/a
        </td>
      </tr>
      <tr>
        <td class="sub-label">
          Concession Cost of Goods Sold
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.concessionCost / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.concessionCost / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td
          v-if="pnlData.concessionRevenue"
          class="cell-value"
          :class="{ 'text-red': pnlData.concessionCost < 0 }"
        >
          <i18n-n
            :value="pnlData.concessionCost / pnlData.concessionRevenue * 100"
            format="decimal0"
            scope="global"
          />%
        </td>
        <td
          v-else
          class="cell-value"
        >
          n/a
        </td>
      </tr>
      <tr>
        <td
          class="bg-grey-2"
          colspan="4"
        />
      </tr>

      <!-- Contribution -->
      <tr class="total-row">
        <td class="bold-label">
          Gross Profit
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.grossProfit / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.grossProfit / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td
          v-if="pnlData.revenue"
          class="cell-value"
          :class="{ 'text-red': pnlData.grossProfit < 0 }"
        >
          <i18n-n
            :value="pnlData.grossProfit / pnlData.revenue * 100"
            format="decimal0"
            scope="global"
          />%
        </td>
        <td
          v-else
          class="cell-value"
        >
          n/a
        </td>
      </tr>
      <tr>
        <td class="sub-label">
          Box Office Gross Profit
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.boxOfficeGrossProfit / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.boxOfficeGrossProfit / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td
          v-if="pnlData.boxOfficeRevenue"
          class="cell-value"
          :class="{ 'text-red': pnlData.boxOfficeGrossProfit < 0 }"
        >
          <i18n-n
            :value="pnlData.boxOfficeGrossProfit / pnlData.boxOfficeRevenue * 100"
            format="decimal0"
            scope="global"
          />%
        </td>
        <td
          v-else
          class="cell-value"
        >
          n/a
        </td>
      </tr>
      <tr>
        <td class="sub-label">
          Concession Gross Profit
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.concessionGrossProfit / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.concessionGrossProfit / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td
          v-if="pnlData.concessionRevenue"
          class="cell-value"
          :class="{ 'text-red': pnlData.concessionGrossProfit < 0 }"
        >
          <i18n-n
            :value="pnlData.concessionGrossProfit / pnlData.concessionRevenue * 100"
            format="decimal0"
            scope="global"
          />%
        </td>
        <td
          v-else
          class="cell-value"
        >
          n/a
        </td>
      </tr>
      <tr>
        <td
          class="bg-grey-2"
          colspan="4"
        />
      </tr>

      <!-- Labor -->
      <tr>
        <td>
          {{ $_.startCase(pnlData.pnlConfigValue(PNL_CONFIG_KEYS.LABOR_MODE)) }} Labor ({{ $i18n.n(pnlData.laborHours, 'decimal1') }} hours)
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.laborCost / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.laborCost / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr class="grand-total-row">
        <td>
          Post Labor Profit
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.postLaborProfit / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.postLaborProfit / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td
          v-if="pnlData.revenue"
          class="cell-value"
          :class="{ 'text-red': pnlData.postLaborProfit < 0 }"
        >
          <i18n-n
            :value="pnlData.postLaborProfit / pnlData.revenue * 100"
            format="decimal0"
            scope="global"
          />%
        </td>
        <td
          v-else
          class="cell-value"
        >
          n/a
        </td>
      </tr>

      <tr>
        <td
          class="bg-grey-2"
          colspan="4"
        />
      </tr>
      <tr class="total-row">
        <td class="bold-label">
          Variable Costs
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.variableCost / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.variableCost / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td
          v-if="pnlData.revenue"
          class="cell-value"
          :class="{ 'text-red': pnlData.variableCost < 0 }"
        >
          <i18n-n
            :value="pnlData.variableCost / pnlData.revenue * 100"
            format="decimal0"
            scope="global"
          />%
        </td>
        <td
          v-else
          class="cell-value"
        >
          n/a
        </td>
      </tr>

      <tr>
        <td>
          {{ variableOverhead }}
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.variableOverheadCost / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.variableOverheadCost / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr v-if="pnlData.otherShowingCosts">
        <td>
          Other Per Showing Costs (<i18n-n
            :value="pnlData.otherShowingCosts / pnlData.showingCount / 100"
            format="currency"
            scope="global"
          /> × {{ pnlData.showingCount }} showings)
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.otherShowingCosts / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.otherShowingCosts / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr v-if="pnlData.otherShowingHourCosts">
        <td>
          Other Per Showing Hour Costs (<i18n-n
            :value="pnlData.otherShowingHourCosts / pnlData.durationHours / 100"
            format="currency"
            scope="global"
          /> × {{ $i18n.n(pnlData.durationHours, 'decimal1') }} hours)
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.otherShowingHourCosts / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.otherShowingHourCosts / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr v-if="pnlData.other_ticket_costs">
        <td>
          Other Per Ticket Costs (<i18n-n
            :value="pnlData.other_ticket_costs / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          /> × {{ $i18n.n(pnlData.ticketsSold, 'decimal0') }} tickets)
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.other_ticket_costs / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.other_ticket_costs / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr class="grand-total-row">
        <td>
          Contribution Margin
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.contributionMargin / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.contributionMargin / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td
          v-if="pnlData.revenue"
          class="cell-value"
          :class="{ 'text-red': pnlData.contributionMargin < 0 }"
        >
          <i18n-n
            :value="pnlData.contributionMargin / pnlData.revenue * 100"
            format="decimal0"
            scope="global"
          />%
        </td>
        <td
          v-else
          class="cell-value"
        >
          n/a
        </td>
      </tr>
      <tr>
        <td
          class="bg-grey-2"
          colspan="4"
        />
      </tr>
      <tr>
        <td>
          {{ fixedOverhead }}
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.fixedOverheadCost / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.fixedOverheadCost / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td />
      </tr>
      <tr class="grand-total-row">
        <td>
          Operating Profit
        </td>
        <td class="cell-value">
          <i18n-n
            :value="pnlData.operatingProfit / 100"
            format="currencyNoCents"
            scope="global"
          />
        </td>
        <td class="cell-value">
          <i18n-n
            v-if="pnlData.ticketsSold"
            :value="pnlData.operatingProfit / pnlData.ticketsSold / 100"
            format="currency"
            scope="global"
          />
          <div v-else>
            n/a
          </div>
        </td>
        <td
          v-if="pnlData.revenue"
          class="cell-value"
          :class="{ 'text-red': pnlData.operatingProfit < 0 }"
        >
          <i18n-n
            :value="pnlData.operatingProfit / pnlData.revenue * 100"
            format="decimal0"
            scope="global"
          />%
        </td>
        <td
          v-else
          class="cell-value"
        >
          n/a
        </td>
      </tr>
    </tbody>
  </q-markup-table>
</template>

<script>

import { PNL_CONFIG_KEYS } from 'src/models/pnl-data/PnlData'

export default {
  name: 'ProfitAndLossTable',
  props: {
    pnlData: {
      type: Object,
      required: true
    },
    size: {
      type: String,
      required: false,
      default: 'md'
    }
  },
  computed: {
    variableOverhead () {
      const overheadDetails = []

      overheadDetails.push('Variable Overhead')
      overheadDetails.push(`(${this.$i18n.n(this.pnlData.variableOverheadRate, 'decimal1')}${this.currentCircuit.localeCurrencyHundredthsSymbol}`)
      overheadDetails.push(`× ${this.$i18n.n(this.pnlData.averageDurationHours, 'decimal1')} hours`)
      overheadDetails.push(`× ${this.$i18n.n(this.pnlData.averageSeatCount, this.pnlData.showingCount > 1 ? 'decimal1' : 'decimal0')} seats`)
      if (this.pnlData.showingCount > 1) {
        overheadDetails.push(` x ${this.$i18n.n(this.pnlData.showingCount, 'decimal0')} showings`)
      }
      overheadDetails.push(')')

      return overheadDetails.join(' ')
    },
    fixedOverhead () {
      const overheadDetails = []

      overheadDetails.push('Fixed Overhead')
      overheadDetails.push(`(${this.$i18n.n(this.pnlData.fixedOverheadRate, 'decimal1')}${this.currentCircuit.localeCurrencyHundredthsSymbol}`)
      overheadDetails.push(`× ${this.$i18n.n(this.pnlData.averageDurationHours, 'decimal1')} hours`)
      overheadDetails.push(`× ${this.$i18n.n(this.pnlData.averageSeatCount, this.pnlData.showingCount > 1 ? 'decimal1' : 'decimal0')} seats`)
      if (this.pnlData.showingCount > 1) {
        overheadDetails.push(` x ${this.$i18n.n(this.pnlData.showingCount, 'decimal0')} showings`)
      }
      overheadDetails.push(')')

      return overheadDetails.join(' ')
    }
  },
  created () {
    _.extend(this, { PNL_CONFIG_KEYS })
  },
  configuration: ['currentCircuit']
}

</script>

<style lang="scss" scoped>
  .size-sm td, .size-sm th, .size-sm tr {
    font-size: 0.8em !important;
    padding: 2px 6px !important;
    height: 5px !important;
  }
  .size-md td, .size-md th, .size-md tr {
    padding: 2px 6px !important;
    height: 10px !important;
  }
  th, .bold-label {
    font-weight: bold;
  }
  .sub-label {
    padding-left: 1em;
  }
  .cell-value {
    text-align: right;
  }
  .total-row td {
    border-bottom: solid 1px #444;
  }
  .grand-total-row td {
    font-weight: bold;
    border-top: double 3px #444;
  }
</style>
